import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { CreditCard, Truck, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { createOrder, createCheckoutSession } from "../lib/api";

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  postal_code: "",
  notes: "",
};

export const CheckoutPage = () => {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState(initialForm);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.error("Η πληρωμή ακυρώθηκε. Μπορείτε να δοκιμάσετε ξανά.");
    }
  }, [searchParams]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit =
    items.length > 0 &&
    form.full_name &&
    form.email &&
    form.phone &&
    form.address &&
    form.city &&
    form.postal_code;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Συμπληρώστε όλα τα υποχρεωτικά πεδία.");
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder({
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
        })),
        customer: form,
        payment_method: paymentMethod,
      });

      if (paymentMethod === "card") {
        const origin_url = window.location.origin;
        const session = await createCheckoutSession({
          order_id: order.id,
          origin_url,
        });
        window.location.href = session.url;
      } else {
        // COD - clear cart and go to status page
        clear();
        navigate(`/payment-status?order_id=${order.id}&method=cod`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Κάτι πήγε στραβά. Δοκιμάστε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="checkout-empty">
        <div className="font-heading text-2xl">Το καλάθι σας είναι άδειο</div>
        <Link to="/artworks" className="inline-flex items-center gap-2 bg-[#B026FF] text-black px-6 py-3 rounded-lg font-bold">
          Δες τα Artworks <ArrowRight size={16} />
        </Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen py-12" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Ολοκλήρωση</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mb-10">
          Checkout
        </h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
          {/* Forms */}
          <div className="flex flex-col gap-6">
            {/* Contact info */}
            <section className="bg-[#050505] p-6 md:p-8 rounded-2xl border border-white/5">
              <h2 className="font-heading font-bold text-xl mb-6">Στοιχεία Παραλαβής</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Ονοματεπώνυμο" required testid="input-name">
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={update("full_name")}
                    className="neon-input"
                    required
                    data-testid="checkout-input-name"
                  />
                </Field>
                <Field label="Email" required testid="input-email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="neon-input"
                    required
                    data-testid="checkout-input-email"
                  />
                </Field>
                <Field label="Τηλέφωνο" required testid="input-phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="neon-input"
                    required
                    data-testid="checkout-input-phone"
                  />
                </Field>
                <Field label="ΤΚ" required testid="input-postal">
                  <input
                    type="text"
                    value={form.postal_code}
                    onChange={update("postal_code")}
                    className="neon-input"
                    required
                    data-testid="checkout-input-postal"
                  />
                </Field>
                <Field label="Διεύθυνση" required testid="input-address" span>
                  <input
                    type="text"
                    value={form.address}
                    onChange={update("address")}
                    className="neon-input"
                    required
                    data-testid="checkout-input-address"
                  />
                </Field>
                <Field label="Πόλη" required testid="input-city">
                  <input
                    type="text"
                    value={form.city}
                    onChange={update("city")}
                    className="neon-input"
                    required
                    data-testid="checkout-input-city"
                  />
                </Field>
                <Field label="Σημειώσεις" testid="input-notes" span>
                  <textarea
                    value={form.notes}
                    onChange={update("notes")}
                    rows={3}
                    className="neon-input resize-none"
                    data-testid="checkout-input-notes"
                  />
                </Field>
              </div>
            </section>

            {/* Payment */}
            <section className="bg-[#050505] p-6 md:p-8 rounded-2xl border border-white/5">
              <h2 className="font-heading font-bold text-xl mb-6">Τρόπος Πληρωμής</h2>
              <div className="flex flex-col gap-3">
                <PaymentOption
                  selected={paymentMethod === "card"}
                  onSelect={() => setPaymentMethod("card")}
                  icon={CreditCard}
                  title="Πιστωτική / Χρεωστική Κάρτα"
                  desc="Ασφαλής πληρωμή μέσω Stripe"
                  testid="payment-option-card"
                />
                <PaymentOption
                  selected={paymentMethod === "cod"}
                  onSelect={() => setPaymentMethod("cod")}
                  icon={Truck}
                  title="Αντικαταβολή"
                  desc="Πληρωμή κατά την παράδοση (+3,00€)"
                  testid="payment-option-cod"
                />
              </div>
            </section>
          </div>

          {/* Summary */}
          <aside className="bg-[#050505] p-6 md:p-8 rounded-2xl border border-white/5 h-max sticky top-28" data-testid="checkout-summary">
            <h2 className="font-heading font-bold text-xl mb-6">Παραγγελία</h2>
            <div className="flex flex-col gap-3 max-h-64 overflow-y-auto pr-2">
              {items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-3">
                  <img src={i.image} alt={i.name} className="w-14 h-14 rounded-lg object-cover bg-black" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">{i.name}</div>
                    <div className="text-xs text-zinc-500">×{i.quantity}</div>
                  </div>
                  <div className="text-sm text-[#00FFFF] font-semibold">
                    {(i.price * i.quantity).toFixed(2)}€
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/5 mt-6 pt-6 flex flex-col gap-2 text-sm">
              <Row label="Υποσύνολο" value={`${total.toFixed(2)}€`} />
              <Row
                label="Μεταφορικά"
                value={paymentMethod === "cod" ? "+3,00€" : "Δωρεάν"}
              />
            </div>
            <div className="border-t border-white/5 mt-4 pt-4 flex items-center justify-between">
              <span className="text-zinc-400">Σύνολο</span>
              <span className="font-heading font-bold text-2xl text-[#00FFFF]" data-testid="checkout-total">
                {(total + (paymentMethod === "cod" ? 3 : 0)).toFixed(2)}€
              </span>
            </div>

            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full mt-6 bg-[#B026FF] text-black font-bold py-4 rounded-lg hover:neon-box-glow-pink transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="checkout-submit"
            >
              {submitting ? (
                "Παρακαλώ..."
              ) : paymentMethod === "card" ? (
                <><Lock size={16} /> Πληρωμή με κάρτα</>
              ) : (
                <>Ολοκλήρωση με αντικαταβολή <ArrowRight size={16} /></>
              )}
            </button>
            <p className="text-[10px] text-zinc-600 text-center mt-3">
              Με την ολοκλήρωση της παραγγελίας αποδέχεστε τους όρους χρήσης.
            </p>
          </aside>
        </form>
      </div>

      <style>{`
        .neon-input {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.08);
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .neon-input:focus {
          border-color: #B026FF;
          box-shadow: 0 0 0 1px #B026FF;
        }
      `}</style>
    </main>
  );
};

const Field = ({ label, required, children, span }) => (
  <label className={`flex flex-col gap-2 ${span ? "sm:col-span-2" : ""}`}>
    <span className="text-xs text-zinc-400 font-medium">
      {label} {required && <span className="text-[#B026FF]">*</span>}
    </span>
    {children}
  </label>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between text-zinc-400">
    <span>{label}</span>
    <span className="text-white">{value}</span>
  </div>
);

const PaymentOption = ({ selected, onSelect, icon: Icon, title, desc, testid }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${
      selected
        ? "border-[#B026FF] bg-[#B026FF]/5 shadow-[0_0_20px_rgba(176,38,255,0.15)]"
        : "border-white/5 hover:border-white/20"
    }`}
    data-testid={testid}
  >
    <div className={`p-2 rounded-lg ${selected ? "bg-[#B026FF] text-black" : "bg-white/5 text-zinc-300"}`}>
      <Icon size={18} />
    </div>
    <div className="flex-1">
      <div className="font-semibold">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{desc}</div>
    </div>
    <div
      className={`w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${
        selected ? "border-[#B026FF]" : "border-white/20"
      }`}
    >
      {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#B026FF]" />}
    </div>
  </button>
);

export default CheckoutPage;
